import {
  Grid,
  GridProps,
  Slide,
  TypographyProps,
  Typography,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  CampaignsIcon,
  ResourcesIcon,
  SidebarLogoutIcon,
  SidebarProfileIcon,
} from 'assets/icons'
import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import { useAuth } from 'utils/providers/AuthProvider'
import { SupportDrawer } from 'components/SupportDrawer'
import Onboarding from 'components/wrappers/MainWrapper/Onboarding'

const useStyles = makeStyles((theme) => {
  return {
    container: {
      height: 'calc(100dvh - 64px)',
      width: '100vw',
      padding: '24px 16px',
      backgroundColor: theme.palette.primary.dark,
      position: 'fixed',
      top: '63px',
      right: 0,
      zIndex: 1000,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      '& a': {
        textDecoration: 'none',
      },
    },

    icon: {
      fill: 'transparent',
      stroke: theme.palette.primary.main,
      '& path': {
        stroke: theme.palette.common.white,
      },
    },
    iconActive: {
      '& path': {
        stroke: theme.palette.primary.main,
        fill: 'transparent',
      },
    },
  }
})

const SidebarItem = ({
  sx,
  children,
  active,
  ...props
}: GridProps & { active?: boolean }) => {
  return (
    <Grid
      container
      alignItems="center"
      gap="8px"
      sx={{
        padding: '12px',
        borderRadius: '10px',
        backgroundColor: active
          ? (theme) => theme.palette.secondary.main
          : 'transparent',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Grid>
  )
}

const SidebarItemTitle = ({
  children,
  sx,
  active,
  ...props
}: TypographyProps & { active?: boolean }) => {
  return (
    <Typography
      fontSize="14px"
      fontWeight={active ? 700 : 500}
      sx={{
        textDecoration: 'none',
        color: (theme) =>
          active ? theme.palette.primary.main : theme.palette.secondary.light,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  )
}

const SideMenu = ({
  open,
  setOpen,
}: {
  open: boolean
  setOpen: (open: boolean) => void
}) => {
  const classes = useStyles()
  const { logout, isLoggingOut } = useAuth()
  const [supportOpened, setSupportOpened] = useState(false)

  const handleLinkClick = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return
    }
    document.body.style.overflow = 'auto'
  }, [open])

  return (
    <>
      <Slide direction="right" in={open}>
        <Grid className={classes.container}>
          <Grid container flexDirection="column" gap="12px">
            <NavLink onClick={handleLinkClick} to="/profile">
              {({ isActive }) => {
                return (
                  <SidebarItem active={isActive}>
                    <SidebarProfileIcon
                      sx={{
                        fill: 'transparent',
                        '& path': {
                          stroke: (theme) =>
                            isActive ? theme.palette.primary.main : 'white',
                        },
                      }}
                    />
                    <SidebarItemTitle active={isActive}>
                      My Profile
                    </SidebarItemTitle>
                  </SidebarItem>
                )
              }}
            </NavLink>
            <NavLink onClick={handleLinkClick} to="/campaigns">
              {({ isActive }) => {
                return (
                  <SidebarItem active={isActive}>
                    <CampaignsIcon
                      sx={{
                        '& path': {
                          fill: (theme) =>
                            isActive ? theme.palette.primary.main : 'white',
                        },
                      }}
                    />
                    <SidebarItemTitle active={isActive}>
                      Campaigns
                    </SidebarItemTitle>
                  </SidebarItem>
                )
              }}
            </NavLink>
            <NavLink onClick={handleLinkClick} to="/resources">
              {({ isActive }) => {
                return (
                  <SidebarItem active={isActive}>
                    <ResourcesIcon
                      sx={{
                        '& path': {
                          fill: (theme) =>
                            isActive ? theme.palette.primary.main : 'white',
                        },
                      }}
                    />
                    <SidebarItemTitle active={isActive}>
                      Resources
                    </SidebarItemTitle>
                  </SidebarItem>
                )
              }}
            </NavLink>
            <SidebarItem
              onClick={() => {
                setOpen(false)
                setSupportOpened(true)
              }}
            >
              <HelpOutlineOutlinedIcon sx={{ fill: 'white' }} />
              <SidebarItemTitle>Support</SidebarItemTitle>
            </SidebarItem>
            <SidebarItem onClick={logout}>
              <SidebarLogoutIcon />
              <SidebarItemTitle>Log out</SidebarItemTitle>
              {isLoggingOut ? (
                <CircularProgress
                  sx={{ ml: 1 }}
                  color="secondary"
                  size="20px"
                />
              ) : null}
            </SidebarItem>
          </Grid>
          <Grid>
            <Onboarding />
          </Grid>
        </Grid>
      </Slide>
      <SupportDrawer open={supportOpened} setOpen={setSupportOpened} />
    </>
  )
}

export default SideMenu
